import { ModelEntityType } from '@upright/common/model/input/types';
import {
  GetProductSdgAlignmentsRequestParams,
  GetSdgAlignmentsDrilldownResponse,
  GetSdgAlignmentsRequestParams,
  GetSdgAlignmentsResponse,
} from '@upright/common/model/output/scores/sdg-alignment/api-types';
import { SdgAlignmentValence, SdgId } from '@upright/common/model/output/scores/sdg-alignment/types';
import { PartialBy } from '@upright/common/types';
import { ErrorWithHttpStatus } from '@upright/ui/api/common/error-with-http-status';
import { fetchOrThrow } from '@upright/ui/api/common/fetchers/fetch-or-throw';
import { isNumber, isString } from 'lodash';
import { useQuery } from '@tanstack/react-query';
import { validate as isUuid } from '@upright/common/utils/uuid';

type RawGetSdgAlignmentsRequestParams =
  | (PartialBy<GetSdgAlignmentsRequestParams, 'resultOrVariantId'> & { release: string | null })
  | GetProductSdgAlignmentsRequestParams;

interface SdgDrillDownQuery {
  entityType: GetSdgAlignmentsRequestParams['entityType'];
  entityId: string;
  sdgId: SdgId;
  valence: SdgAlignmentValence;
  resultId: number;
  release: string;
}

export function useFetchSdgAlignments(params: RawGetSdgAlignmentsRequestParams) {
  const getter =
    params.entityType === ModelEntityType.PRODUCT
      ? async () => await fetchProductSdgAlignments(params)
      : async () => await fetchSdgAlignments(params as GetSdgAlignmentsRequestParams);
  return useQuery({
    queryKey: ['sdgAlignments', params],
    queryFn: getter,
    enabled: validateSdgAlignmentsRequest(params),
    staleTime: 120 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useFetchSdgDrillDownData(query: PartialBy<SdgDrillDownQuery, 'resultId'>) {
  const { entityType, entityId, resultId, sdgId, valence, release } = query;
  return useQuery<GetSdgAlignmentsDrilldownResponse, ErrorWithHttpStatus>({
    queryKey: ['sdgDrilldown', entityType, entityId, resultId, sdgId, valence, release],
    queryFn: async () => await fetchSdgDrillDownData(query as SdgDrillDownQuery),
    staleTime: 120 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
    enabled: !!resultId,
  });
}

async function fetchSdgAlignments(params: GetSdgAlignmentsRequestParams) {
  const { release, entityType, entityId, resultOrVariantId } = params;
  const path = `/sdg-alignments/${release}/${entityType}/${entityId}/${resultOrVariantId}`;
  return await fetchOrThrow<GetSdgAlignmentsResponse>(path, 'GET');
}

async function fetchProductSdgAlignments(params: GetProductSdgAlignmentsRequestParams) {
  const { version, productCode } = params;
  const path = `/product-sdg-alignments/${version}/${productCode}`;
  return await fetchOrThrow<GetSdgAlignmentsResponse>(path, 'GET');
}

// N.B.: duplicated in backend handler validation in src/services/main/upserve/modules/sdg-alignments/index.ts
// Rationale: as of this writing, legacy code likes to query company results with ID -1
// if the result ID is missing => this validation ensures we don't make calls to the backend with
// invalid parameters
function validateSdgAlignmentsRequest(
  params: RawGetSdgAlignmentsRequestParams,
): params is GetSdgAlignmentsRequestParams {
  switch (params.entityType) {
    case ModelEntityType.PRODUCT:
      return true;
    case ModelEntityType.COMPANY:
      return isString(params.resultOrVariantId) && isUuid(params.resultOrVariantId) && isString(params.release);
    case ModelEntityType.PORTFOLIO:
    case ModelEntityType.GROUP:
      return isNumber(params.resultOrVariantId);
    default:
      return false;
  }
}

async function fetchSdgDrillDownData(query: SdgDrillDownQuery) {
  const { entityType, entityId, resultId, sdgId, valence, release } = query;
  return await fetchOrThrow<GetSdgAlignmentsDrilldownResponse>(
    `/sdg-alignments/drilldown/${release}/${entityType}/${entityId}/${resultId}/${sdgId}/${valence}`,
    'GET',
  );
}
